<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="700px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card height="440">
            <v-card-title>
                <h1 class="text-subtitle-1">
                    <template>
                        Ustawienia kolorystyki {{ side }}
                    </template>
                </h1>
            </v-card-title>
            <v-card-text>
                <v-menu
                    offset-y
                    max-height="410"
                >
                    <template #activator="{on,attrs}">
                        <v-btn
                            color="secondary"
                            v-bind="attrs"
                            depressed
                            data-sentry-label="Dodaj"
                            :disabled="availableColors.length === 0 || !isJobEditable"
                            v-on="on"
                        >
                            <v-icon left>
                                mdi-plus
                            </v-icon>
                            Dodaj
                        </v-btn>
                    </template>
                    <v-list dense>
                        <v-list-item
                            v-for="(color, index) in availableColors"
                            :key="index"
                            @click="addColor(color)"
                        >
                            <v-list-item-title>{{ color.name }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>

                <v-data-table
                    :items="colors"
                    :headers="headers"
                    disable-pagination
                    hide-default-footer
                    :mobile-breakpoint="0"
                    no-data-text="Nie dodano kolorów"
                >
                    <template #item.type="{item}">
                        {{ item.jobPrintColour.type }}
                    </template>
                    <template #item.name="{item}">
                        {{ item.name }}
                    </template>
                    <template #item.coverage="{item}">
                        <v-edit-dialog
                            :return-value.sync="item.usedPercent"
                            large
                            cancel-text="Anuluj"
                            save-text="Zapisz"
                        >
                            {{ item.usedPercent }}
                            <v-icon color="blue" small :disabled="!isJobEditable">
                                mdi-pencil
                            </v-icon>
                            <template #input>
                                <v-text-field
                                    v-model="item.usedPercent"
                                    v-mask="'###'"
                                    single-line
                                />
                            </template>
                        </v-edit-dialog>
                    </template>
                    <template #item.description="{item}">
                        <v-edit-dialog
                            :return-value.sync="item.description"
                            large
                            cancel-text="Anuluj"
                            save-text="Zapisz"
                        >
                            {{ item.description }}
                            <v-icon color="blue" small :disabled="!isJobEditable">
                                mdi-pencil
                            </v-icon>
                            <template #input>
                                <v-text-field
                                    v-model="item.description"
                                    single-line
                                />
                            </template>
                        </v-edit-dialog>
                    </template>
                    <template #item.actions="{item}">
                        <div class="d-inline-flex">
                            <v-tooltip :open-delay="tooltipDelay" top>
                                <template #activator="{on}">
                                    <v-btn
                                        icon
                                        data-sentry-label="Usuń"
                                        :disabled="!isJobEditable"
                                        v-on="on"
                                        @click="removeColor(item)"
                                    >
                                        <v-icon color="pink">
                                            mdi-delete
                                        </v-icon>
                                    </v-btn>
                                </template>
                                <span>Usuń</span>
                            </v-tooltip>
                        </div>
                    </template>
                </v-data-table>
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Anuluj"
                    @click="onClose"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    color="success"
                    text
                    data-sentry-label="Zapisz"
                    @click="onSave"
                >
                    Zapisz
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { appConfig } from '@/config'
import tooltipDelay from '@/mixins/tooltip'
import { mask } from 'vue-the-mask'

export default {
    name: 'ColorSettingsDialog',
    directives: { mask },
    mixins: [ tooltipDelay ],
    props: {
        isOpen: {
            type: Boolean
        },
        sideData: {
            type: String,
            default: null
        },
        impositionData: {
            type: Object,
            default: null
        },
        jobPrintColors: {
            type: Array,
            default: null
        },
        taskData: {
            type: Object,
            default: null
        },
        isJobEditable: {
            type: Boolean
        },
    },
    data() {
        return {
            colors: [],
            side: null,
            headers: [
                {
                    text: 'Typ',
                    value: 'type',
                    align: 'left',
                    sortable: false,
                    width: 120
                },
                {
                    text: 'Kolor',
                    value: 'name',
                    align: 'center',
                    sortable: false,
                    width: 150
                },
                {
                    text: 'Pokrycie (%)',
                    value: 'coverage',
                    align: 'center',
                    sortable: false,
                    width: 110
                },
                {
                    text: 'Opis',
                    value: 'description',
                    align: 'left',
                    sortable: false,
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'right',
                    sortable: false,
                    width: 100
                },
            ]
        }
    },
    computed: {
        availableColors() {

            const colors = JSON.parse(JSON.stringify(this.$store.state.jobs.jobs.jobPrintColors))
            const CMYKList = ['C', 'Y', 'M', 'K']
            const colorsNames = []
            
            colors.forEach(color => colorsNames.push(color.name))
            
            if (colors.length >= 4 && CMYKList.every((element) => colorsNames.includes(element))) {
                colors.unshift({
                    name: 'CMYK'
                })
            }

            return colors
        }
    },
    watch: {
        isOpen(boolean) {
            if (boolean) {
                this.side = this.sideData
                const colorKey = `impositionColours${this.side}`

                this.colors = JSON.parse(JSON.stringify(this.impositionData[colorKey]))
                this.handleColors()
            }
        }
    },
    methods: {
        addColor(color) {
            if (color.name === 'CMYK') {
                this.addAllCMYKColors()
            } else {
                this.colors.push({
                    jobPrintColour: color,
                    jobPrintColourId: color.id,
                    side: this.side,
                    name: color.name,
                    description: null,
                    usedPercent: this.setDefaultUsedPercentHandler(color.type)
                })
            }

            this.handleColors()
        },
        removeColor(color) {
            this.colors.splice(color.index, 1)
            this.handleColors()
        },
        addAllCMYKColors() {
            const basicColors = this.jobPrintColors.filter(item => item.type === 'zwyczajny')

            this.colors = this.colors.concat(basicColors.map(color => {
                return {
                    jobPrintColour: color,
                    jobPrintColourId: color.id,
                    side: this.side,
                    name: color.name,
                    description: null,
                    usedPercent: this.setDefaultUsedPercentHandler(color.type)
                }
            }))
        },
        setDefaultUsedPercentHandler(colorType) {
            switch (colorType) {
                case 'zwyczajny':
                    return appConfig.common.defaultColorUsedPercents.basic
                case 'PMS':
                    return appConfig.common.defaultColorUsedPercents.pms
                case 'lakier':
                    return appConfig.common.defaultColorUsedPercents.lacquer
                default:
                    return 0
            }
        },
        handleColors() {
            let specialColorCount = 0

            this.colors = this.colors.map((color, index) => {
                const isSpecialColor = color.jobPrintColour.id === 6 // Pantone

                if (isSpecialColor) specialColorCount++

                return {
                    ...color,
                    name: isSpecialColor ? `${color.name.split(' ')[0]} ${specialColorCount}` : color.name,
                    index
                }
            })
        },
        onSave() {
            this.$emit('on-save', {
                imposition: this.impositionData,
                colorSettings: this.colors,
                side: this.side
            })

            this.onClose()
        },
        onClose() {
            setTimeout(() => {
                this.colors = []
                this.side = null
            }, 500)

            this.$emit('on-close')
        }
    }
}
</script>